import type { AppRouter } from "@repo/api/trpc";
import * as Sentry from "@sentry/react";
import type { TRPCLink } from "@trpc/client";
import {
  createTRPCProxyClient,
  httpBatchLink,
  httpSubscriptionLink,
  splitLink,
  TRPCClientError,
} from "@trpc/client";
import type {
  AnyRouter,
  inferRouterInputs,
  inferRouterOutputs,
} from "@trpc/server";
import { observable } from "@trpc/server/observable";
import superjson from "superjson";

/**
 * tRPC error codes that represent genuine server/transport faults worth
 * reporting to Sentry. Expected, user-correctable client errors
 * (`BAD_REQUEST`, `UNAUTHORIZED`, `FORBIDDEN`, `NOT_FOUND`, `CONFLICT`,
 * `TOO_MANY_REQUESTS`, etc.) are deliberately excluded to keep Sentry signal
 * clean. A missing code (network failure / non-tRPC error) is also captured.
 */
const SENTRY_REPORTABLE_CODES = new Set([
  "INTERNAL_SERVER_ERROR",
  "BAD_GATEWAY",
  "SERVICE_UNAVAILABLE",
  "TIMEOUT",
]);

/**
 * Normalizes `TRPCClientError.message` to the clean, user-facing server
 * message so any consumer (toasts, error boundaries) can render
 * `error.message` directly without the procedure path leaking through. The
 * failed procedure path is preserved on `error.procedurePath` for debugging
 * instead of being prefixed onto the message.
 */
const readableErrorLink: TRPCLink<AnyRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next: (value) => observer.next(value),
        error: (err) => {
          if (err instanceof TRPCClientError) {
            const serverMessage = err.shape?.message;
            if (serverMessage) {
              err.message = serverMessage;
            }
            (
              err as TRPCClientError<AnyRouter> & ProcedureTaggedError
            ).procedurePath = op.path;

            // Report only genuine server/transport faults; expected
            // client errors (validation, auth, not-found) stay out of Sentry.
            const code = err.data?.code as string | undefined;
            if (!code || SENTRY_REPORTABLE_CODES.has(code)) {
              Sentry.captureException(err, {
                tags: { procedurePath: op.path, trpcCode: code ?? "UNKNOWN" },
              });
            }
          } else {
            // Non-tRPC failures (e.g. network errors) are always unexpected.
            Sentry.captureException(err, { tags: { procedurePath: op.path } });
          }
          observer.error(err);
        },
        complete: () => observer.complete(),
      });
      return unsubscribe;
    });
  };
};

/** Marker mixed onto tRPC client errors recording which procedure failed. */
interface ProcedureTaggedError {
  procedurePath?: string;
}

const API_URL = `${import.meta.env.VITE_DOMAIN_API}/trpc`;

export const trpc = createTRPCProxyClient<AppRouter>({
  links: [
    readableErrorLink,
    splitLink({
      condition: (op) => op.type === "subscription",
      true: httpSubscriptionLink({
        url: API_URL,
        transformer: superjson,
        eventSourceOptions: { withCredentials: true },
      }),
      false: httpBatchLink({
        url: API_URL,
        transformer: superjson,
        fetch: (url, options) =>
          fetch(url, {
            ...options,
            credentials: "include",
          }),
      }),
    }),
  ],
});

export type RouterInput = inferRouterInputs<AppRouter>;
export type RouterOutput = inferRouterOutputs<AppRouter>;
